import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import * as transportService from "../service/transportService";
import type { TransportState } from "../types";
import i18n from "@/i18n";

export const fetchRoutes = createAsyncThunk("transport/fetchRoutes", async (institutionId: number) => {
  return await transportService.fetchRoutes(institutionId);
});

export const createRoute = createAsyncThunk(
  "transport/createRoute",
  async (
    {
      institutionId,
      payload,
    }: { institutionId: number; payload: { routeName: string; vehicleNumber?: string; driverStaffId?: number | null; driverName?: string; driverPhone?: string; capacity?: number } },
    { dispatch }
  ) => {
    await transportService.createRoute(institutionId, payload);
    await dispatch(fetchRoutes(institutionId));
  }
);

export const assignStudent = createAsyncThunk(
  "transport/assignStudent",
  async (
    { institutionId, payload }: { institutionId: number; payload: { routeId: number; studentId: number; pickupPoint?: string; stopId?: number; academicYearId?: number } },
    { dispatch }
  ) => {
    await transportService.assignStudent(institutionId, payload);
    await dispatch(fetchRoutes(institutionId));
  }
);

export const endAssignment = createAsyncThunk(
  "transport/endAssignment",
  async (
    { institutionId, assignmentId, studentId, academicYearId }: { institutionId: number; assignmentId: number; studentId: number; academicYearId?: number },
    { dispatch }
  ) => {
    await transportService.endAssignment(institutionId, assignmentId);
    await dispatch(fetchStudentAssignments({ institutionId, studentId, academicYearId }));
  }
);

export const fetchStudentAssignments = createAsyncThunk(
  "transport/fetchStudentAssignments",
  async ({ institutionId, studentId, academicYearId }: { institutionId: number; studentId: number; academicYearId?: number }) => {
    return await transportService.fetchStudentAssignments(institutionId, studentId, academicYearId);
  }
);

const initialState: TransportState = {
  routes: [],
  studentAssignments: [],
  loading: false,
  saving: false,
  error: null,
};

const transportSlice = createSlice({
  name: "transport",
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(fetchRoutes.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchRoutes.fulfilled, (state, action) => {
        state.loading = false;
        state.routes = action.payload;
      })
      .addCase(fetchRoutes.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message ?? i18n.t("facilities:errors.failedToLoadRoutes");
      })
      .addCase(createRoute.rejected, (state, action) => {
        state.error = action.error.message ?? i18n.t("facilities:errors.failedToAddRoute");
      })
      .addCase(assignStudent.pending, (state) => {
        state.saving = true;
        state.error = null;
      })
      .addCase(assignStudent.fulfilled, (state) => {
        state.saving = false;
      })
      .addCase(assignStudent.rejected, (state, action) => {
        state.saving = false;
        state.error = action.error.message ?? i18n.t("facilities:errors.failedToAssignStudent");
      })
      .addCase(endAssignment.rejected, (state, action) => {
        state.error = action.error.message ?? i18n.t("facilities:errors.failedToEndAssignment");
      })
      .addCase(fetchStudentAssignments.fulfilled, (state, action) => {
        state.studentAssignments = action.payload;
      });
  },
});

export default transportSlice.reducer;
